import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, API } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ProposalTab } from '../components/proposal/ProposalTab';
import { ReviewCard } from '../components/ReviewCard';
import { Mic, Video as VideoIcon, Send, Square, Play, ArrowLeft, ExternalLink, Download, FileText, Paperclip, BookOpen, X } from 'lucide-react';

export default function ConsultationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [recording, setRecording] = useState(null); // 'audio' | 'video' | null
  const [showBlog, setShowBlog] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('tab') === 'propuesta' ? 'proposal' : 'chat');
  const fileInputRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const previewRef = useRef(null);
  const scrollRef = useRef(null);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get(`/consultations/${id}`),
      api.get(`/consultations/${id}/messages`, { params: { mark_read: tab === 'chat' } }),
    ]);
    setC(a.data); setMsgs(b.data);
    if (tab === 'chat') window.dispatchEvent(new Event('stilo:unread-refresh'));
    setTimeout(() => scrollRef.current?.scrollTo({ top: 9999, behavior: 'smooth' }), 50);
  };

  const openBlog = async () => {
    setShowBlog(true);
    if (blogPosts.length === 0) {
      try {
        const r = await api.get('/blog/posts?limit=6');
        setBlogPosts(r.data);
      } catch {}
    }
  };

  const sharePost = async (p) => {
    const body = `📖 Te recomiendo leer: "${p.title}"\n${p.link}`;
    const r = await api.post(`/consultations/${id}/messages`, { kind: 'text', content: body });
    setMsgs(m => [...m, r.data]);
    setShowBlog(false);
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const r = await api.post(`/consultations/${id}/upload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setMsgs(m => [...m, r.data]);
      }
      toast.success(files.length === 1 ? 'Archivo enviado' : `${files.length} archivos enviados`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al subir');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }), 100);
    }
  };

  const fileUrl = (att) => {
    const tk = localStorage.getItem('stilo_token');
    return `${API}/files/${att.file_id}?auth=${encodeURIComponent(tk || '')}`;
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (tab === 'chat' && c) load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendText = async () => {
    if (!text.trim()) return;
    const r = await api.post(`/consultations/${id}/messages`, { kind: 'text', content: text });
    setMsgs(m => [...m, r.data]); setText('');
  };

  const startRec = async (kind) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        kind === 'video' ? { audio: true, video: true } : { audio: true }
      );
      if (kind === 'video' && previewRef.current) { previewRef.current.srcObject = stream; previewRef.current.play(); }
      const mr = new MediaRecorder(stream, { mimeType: kind === 'video' ? 'video/webm' : 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: kind === 'video' ? 'video/webm' : 'audio/webm' });
        const reader = new FileReader();
        reader.onload = async () => {
          const dataUrl = reader.result;
          const r = await api.post(`/consultations/${id}/messages`, { kind, content: dataUrl });
          setMsgs(m => [...m, r.data]);
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
        if (previewRef.current) previewRef.current.srcObject = null;
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(kind);
    } catch (e) {
      toast.error('No se pudo acceder al micro/cámara');
    }
  };
  const stopRec = () => { mediaRef.current?.stop(); setRecording(null); };

  const toggleStatus = async () => {
    const status = c.status === 'closed' ? 'active' : 'closed';
    try {
      const r = await api.post(`/consultations/${id}/status`, { status });
      setC({ ...c, status, ...(status === 'active' ? { auto_close_off: true } : {}) });
      toast.success(status === 'closed' ? 'Conversación finalizada' : 'Conversación reabierta');
      if (r.data.review_email) toast.success('Le he enviado un email para que valore la asesoría');
    } catch {
      toast.error('No se pudo cambiar el estado');
    }
  };

  const chatImages = msgs.filter(m => m.kind === 'image').flatMap(m => (m.attachments || []).map(a => a.file_id));
  const isDesigner = user.role === 'designer';

  if (!c) return <div className="py-32 text-center text-stilo-ink2">Cargando…</div>;

  return (
    <main className="bg-stilo-bg min-h-[80vh]" data-testid="consultation-detail">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 pt-12 pb-32">
        <Link to="/conversaciones" className="inline-flex items-center gap-2 text-stilo-ink2 text-sm mb-6 hover:text-stilo-ink" data-testid="back-to-conversations"><ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Conversaciones</Link>

        <div className="border border-stilo-ink/10 bg-stilo-beige p-8">
          <div className="flex justify-between items-start gap-6 flex-wrap">
            <div>
              <p className="overline">Asesoría · {c.modality === 'live' ? 'Videollamada' : 'Asíncrona'}</p>
              <h1 className="font-serif text-4xl mt-2">{c.package_name}</h1>
              <p className="text-stilo-ink2 mt-2 text-sm">Cliente: {c.user_email}</p>
            </div>
            {c.zoom_join_url && (
              <a href={c.zoom_join_url} target="_blank" rel="noreferrer" className="btn-stilo" data-testid="zoom-join">
                <ExternalLink className="w-4 h-4" strokeWidth={1.5} /> Entrar a Zoom
              </a>
            )}
            {c.modality === 'live' && !c.zoom_join_url && (
              <p className="text-xs text-stilo-ink2 max-w-xs">Enlace Zoom pendiente: configura las claves Zoom en el backend para crear la sala automáticamente.</p>
            )}
            {isDesigner && (
              <button onClick={toggleStatus} className="inline-flex items-center gap-2 px-4 py-2 border border-stilo-ink/20 text-sm hover:border-stilo-ink" data-testid="consultation-status-toggle">
                {c.status === 'closed' ? 'Reabrir conversación' : 'Finalizar conversación'}
              </button>
            )}
          </div>
          {c.status === 'closed' && <p className="mt-4 text-sm text-stilo-ink2" data-testid="consultation-closed-note">{c.closed_reason === 'auto_7d' ? 'Esta conversación se cerró sola al terminar los 7 días de seguimiento.' : 'Esta conversación está finalizada.'}</p>}
          {c.status !== 'closed' && c.modality === 'live' && !c.auto_close_off && (
            <p className="mt-4 text-xs text-stilo-ink3" data-testid="consultation-autoclose-note">Se pasará a «Pasadas» automáticamente el {new Date(new Date(c.created_at).getTime() + 7 * 864e5).toLocaleDateString('es-ES')} (7 días desde el pago).</p>
          )}
        </div>

        <ReviewCard key={c.status} cid={id} isDesigner={isDesigner} closed={c.status === 'closed'} />

        <div className="mt-8 flex gap-8 border-b border-stilo-ink/10" role="tablist">
          {[['chat', 'Conversación'], ['proposal', 'Propuesta']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} role="tab" aria-selected={tab === k} data-testid={`tab-${k}`}
              className={`pb-3 -mb-px font-display text-[11px] tracking-[0.22em] uppercase border-b-2 transition-colors ${tab === k ? 'border-stilo-gold text-stilo-ink' : 'border-transparent text-stilo-ink2 hover:text-stilo-ink'}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'proposal' && <div className="mt-8"><ProposalTab cid={id} isDesigner={isDesigner} chatImages={chatImages} onPublished={load} /></div>}

        {tab === 'chat' && <>
        <div ref={scrollRef} className="mt-8 max-h-[55vh] overflow-y-auto border border-stilo-ink/10 bg-white p-6 space-y-5" data-testid="messages-list">
          {msgs.length === 0 && <p className="text-center text-stilo-ink3 py-12">Empieza la conversación abajo. Puedes mandar texto, audio o vídeo.</p>}
          {msgs.map(m => {
            const mine = m.author_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-4 ${mine ? 'bg-stilo-olive text-stilo-bg' : 'bg-stilo-beige text-stilo-ink'}`}>
                  <p className={`overline mb-2 ${mine ? '!text-stilo-stone' : 'text-stilo-ink2'}`}>{m.author_role === 'designer' ? 'Studio Stilo' : m.author_name}</p>
                  {m.kind === 'text' && <p className="leading-relaxed whitespace-pre-wrap">{m.content}</p>}
                  {m.kind === 'audio' && <audio src={m.content} controls className="w-72 max-w-full" />}
                  {m.kind === 'video' && <video src={m.content} controls className="w-80 max-w-full rounded-sm" />}
                  {m.kind === 'image' && (m.attachments || []).map((a, i) => (
                    <a key={i} href={fileUrl(a)} target="_blank" rel="noreferrer" className="block">
                      <img src={fileUrl(a)} alt={a.filename} className="max-w-[280px] max-h-[360px] rounded-sm object-cover" />
                    </a>
                  ))}
                  {m.kind === 'video_file' && (m.attachments || []).map((a, i) => (
                    <video key={i} src={fileUrl(a)} controls className="max-w-[320px] rounded-sm" />
                  ))}
                  {m.kind === 'pdf' && (m.attachments || []).map((a, i) => (
                    <a key={i} href={fileUrl(a)} target="_blank" rel="noreferrer"
                      className={`flex items-center gap-3 p-3 border rounded-sm hover:opacity-90 ${mine ? 'border-stilo-bg/30 bg-stilo-bg/10' : 'border-stilo-ink/15 bg-stilo-bg'}`}>
                      <FileText className="w-6 h-6" strokeWidth={1.5} />
                      <div className="min-w-0">
                        <p className="font-serif text-base truncate max-w-[220px]">{a.filename}</p>
                        <p className="text-xs opacity-70">{Math.round((a.size || 0) / 1024)} KB · PDF</p>
                      </div>
                      <Download className="w-4 h-4 ml-2" strokeWidth={1.5} />
                    </a>
                  ))}
                  {m.kind === 'file' && (m.attachments || []).map((a, i) => (
                    <a key={i} href={fileUrl(a)} target="_blank" rel="noreferrer" className="underline text-sm">{a.filename}</a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {recording === 'video' && (
          <video ref={previewRef} muted playsInline className="mt-4 w-72 rounded-sm border border-stilo-ink/10" />
        )}

        {c.status === 'closed' && !isDesigner ? (
          <p className="mt-6 border border-stilo-ink/10 bg-stilo-beige p-4 text-sm text-stilo-ink2" data-testid="chat-closed-note">Esta conversación ha terminado. Si necesitas algo más, puedes reservar una nueva asesoría.</p>
        ) : (
        <div className="mt-6 border border-stilo-ink/10 bg-white p-4">
          <div className="flex gap-2 flex-wrap mb-3">
            {!recording && (
              <>
                <button onClick={() => startRec('audio')} className="inline-flex items-center gap-2 px-4 py-2 border border-stilo-ink/15 text-sm hover:border-stilo-ink" data-testid="rec-audio">
                  <Mic className="w-4 h-4" strokeWidth={1.5} /> Audio
                </button>
                <button onClick={() => startRec('video')} className="inline-flex items-center gap-2 px-4 py-2 border border-stilo-ink/15 text-sm hover:border-stilo-ink" data-testid="rec-video">
                  <VideoIcon className="w-4 h-4" strokeWidth={1.5} /> Vídeo
                </button>
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 px-4 py-2 border border-stilo-ink/15 text-sm hover:border-stilo-ink disabled:opacity-50" data-testid="upload-files">
                  <Paperclip className="w-4 h-4" strokeWidth={1.5} /> {uploading ? 'Subiendo…' : 'Adjuntar fotos / vídeos / PDF'}
                </button>
                <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,application/pdf" className="hidden" onChange={onPickFiles} />
                {user.role === 'designer' && (
                  <button onClick={openBlog} className="inline-flex items-center gap-2 px-4 py-2 border border-stilo-ink/15 text-sm hover:border-stilo-ink" data-testid="rec-blog">
                    <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Recomendar post
                  </button>
                )}
              </>
            )}
            {recording && (
              <button onClick={stopRec} className="inline-flex items-center gap-2 px-4 py-2 bg-stilo-terra text-stilo-bg text-sm" data-testid="rec-stop">
                <Square className="w-4 h-4" strokeWidth={1.5} /> Detener {recording === 'video' ? 'vídeo' : 'audio'}
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <textarea
              value={text} onChange={e => setText(e.target.value)} placeholder="Escribe un mensaje…"
              rows={2} className="flex-1 border border-stilo-ink/15 p-3 bg-stilo-bg text-stilo-ink resize-none focus:outline-none focus:border-stilo-ink"
              data-testid="msg-input"
            />
            <button onClick={sendText} className="btn-stilo" data-testid="msg-send">
              <Send className="w-4 h-4" strokeWidth={1.5} /> Enviar
            </button>
          </div>
        </div>
        )}
        </>}
      </div>

      {showBlog && (
        <div className="fixed inset-0 z-50 bg-stilo-ink/60 backdrop-blur-sm grid place-items-center p-6" onClick={() => setShowBlog(false)} data-testid="blog-modal">
          <div className="bg-stilo-bg max-w-3xl w-full p-8 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <p className="overline">Recomendar entrada del blog</p>
              <button onClick={() => setShowBlog(false)} className="text-stilo-ink2 hover:text-stilo-ink" data-testid="blog-close"><X className="w-5 h-5" /></button>
            </div>
            {blogPosts.length === 0 ? (
              <p className="text-stilo-ink2">Cargando entradas…</p>
            ) : (
              <div className="space-y-4">
                {blogPosts.map(p => (
                  <button key={p.id} onClick={() => sharePost(p)} className="w-full flex gap-4 text-left border border-stilo-ink/10 p-4 hover:bg-stilo-beige transition-colors" data-testid={`blog-pick-${p.id}`}>
                    {p.image && <img src={p.image} alt="" className="w-20 h-24 object-cover" />}
                    <div className="flex-1">
                      <p className="font-serif text-xl leading-tight">{p.title}</p>
                      <p className="overline mt-2 text-stilo-ink3">{new Date(p.date).toLocaleDateString('es-ES')}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
